import { useApolloClient } from '@apollo/client'
import {
    SetProjectContentItemIsHiddenInput,
    ViewItemReorderInput,
} from '@deltek/specification-client/core/Models/projectProductFamily'
import { ViewItemReorderPTPInput } from '@deltek/specification-client/core/Models/projectProductTypeProperty'
import {
    SetProjectContentItemIsHiddenCKE,
    SetProjectContentItemIsHiddenBulkCKE,
} from 'api/graphQL/mutations/projectProductFamily.mutations'
import { useCallback } from 'react'
import {
    useReorderFamilyTemplatesMutation,
    useReorderProductTypeTemplatesMutation,
    useShowHideMutation,
} from '../api/mutations/hoveredItemActionsMutations'

export const useHoveredActionMutationService = () => {
    const { setProjectContentItemHidden } = useShowHideMutation()
    const { reorderFamilyTemplates } = useReorderFamilyTemplatesMutation()
    const {
        reorderProductTypeTemplates,
    } = useReorderProductTypeTemplatesMutation()
    const apolloClient = useApolloClient()

    /** Async mutation to update the content item element */
    const mutateDeactivationAsync = useCallback(
        async (
            setProjectContentItemIsHiddenInput: Partial<
                SetProjectContentItemIsHiddenInput
            >
        ) => {
            return await setProjectContentItemHidden({
                variables: {
                    SetProjectContentItemIsHiddenInput: setProjectContentItemIsHiddenInput,
                },
            })
        },
        [setProjectContentItemHidden]
    )

    /** Apollo Client version for single item deactivation (background execution) */
    const mutateDeactivationWithApolloClient = useCallback(
        async (
            setProjectContentItemIsHiddenInput: Partial<
                SetProjectContentItemIsHiddenInput
            >
        ) => {
            return await apolloClient.mutate({
                mutation: SetProjectContentItemIsHiddenCKE,
                variables: {
                    SetProjectContentItemIsHiddenInput: setProjectContentItemIsHiddenInput,
                },
                fetchPolicy: 'no-cache',
            })
        },
        [apolloClient]
    )

    /** Apollo Client version for bulk operations (activates multiple items in single request) */
    const mutateDeactivationBulkWithApolloClient = useCallback(
        async (
            inputs: Partial<SetProjectContentItemIsHiddenInput>[],
            partType?: string
        ) => {
            return await apolloClient.mutate({
                mutation: SetProjectContentItemIsHiddenBulkCKE,
                variables: {
                    inputs,
                    partType,
                },
                fetchPolicy: 'no-cache',
            })
        },
        [apolloClient]
    )

    /** Async mutation to reorder family templates */
    const mutateReorderFamilyTemplatesAsync = useCallback(
        async (reOrderFamilyContentInput: Partial<ViewItemReorderInput>) => {
            return await reorderFamilyTemplates({
                variables: { reOrderFamilyContentInput },
            })
        },
        [reorderFamilyTemplates]
    )

    /** Async mutation to reorder product type templates */
    const mutateReorderProductTypeTemplatesAsync = useCallback(
        async (reOrderPTContentInput: Partial<ViewItemReorderPTPInput>) => {
            return await reorderProductTypeTemplates({
                variables: { reOrderPTContentInput },
            })
        },
        [reorderProductTypeTemplates]
    )

    // Return all the mutations in a single object
    return {
        mutateDeactivationAsync,
        mutateDeactivationWithApolloClient,
        mutateDeactivationBulkWithApolloClient,
        mutateReorderFamilyTemplatesAsync,
        mutateReorderProductTypeTemplatesAsync,
    }
}
